import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridCellParams, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import { useTalononePromoResponse } from '@/hooks/talonone';
import { GridAction } from '@/components/molecules/section/Grid';
import Table from '@/components/molecules/section/Table';
import Container from '@/components/molecules/section/Container';
import Menu, { MenuOption } from '@/components/molecules/common/Menu';
import TooltipIcon from '@/components/svg/Tooltip';
import { dayJsFromString, transformDateString } from '@/utilities/datetime';
import { capitalizeFirstLetter } from '@/utilities/string';
import { TalonOnePromo } from '@/types/talonone';
import { Client } from '@/types/client';
import { Modal } from '@/types/modal';
import ModalView from '@/components/modals/Modal';
import styles from './TalonOnePromoCode.module.scss';
import useTable from '@/hooks/table';
import { useClientActionTracking } from '@/hooks/item';

const defaultPageSize = 5;
const View: React.FC<{ client: Client }> = ({ client }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const [open, setOpen] = React.useState(false);

  const { page, pageSize, totalItems, onPageChange, persistTotalItems } = useTable(defaultPageSize);

  const { data: talons, isFetching } = useTalononePromoResponse(client.clientId, page, pageSize, open);

  const clientTracking = useClientActionTracking(Number(client.clientId));

  useEffect(() => {
    if (talons) persistTotalItems(talons.totalItems);
  }, [talons]);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('client.talon_one_promo_code.actions.new_code'),
      onClick: () =>
        setModal({
          kind: 'TalononeNewPromoCode',
          client,
          pageSize,
          onClose: () => setModal(null),
        }),
    },
    {
      kind: 'Link',
      label: t('client.talon_one_promo_code.actions.coupon_finder'),
      // This url should get from response, but we hard coded to save 1 call to api
      url: 'https://vestiairecollective.europe-west1.talon.one/applications/34/coupons',
      newTab: true,
      onClick: () => clientTracking.mutate('talone_one_coupon_finder'),
    },
  ];

  const menuOption = (row: TalonOnePromo): MenuOption[] => {
    const enabledStatus = row.status === 'enabled';
    const expiryDate = dayJsFromString(row.expiryDateTime, 'DD-MM-YYYY HH:mm:ss');
    const expired = expiryDate ? expiryDate.unix() * 1000 <= Date.now() : false;

    return [
      {
        kind: 'Button',
        label: capitalizeFirstLetter(t('common.extend')) || '',
        onClick: () =>
          setModal({
            kind: 'TalononeExtendPromoCode',
            client,
            promo: row,
            page,
            pageSize,
            onClose: () => setModal(null),
          }),
        disabled: !enabledStatus,
      },
      {
        kind: 'Button',
        label: capitalizeFirstLetter(t('common.disable')) || '',
        onClick: () =>
          setModal({
            kind: 'TalononeDisablePromoCode',
            client,
            promo: row,
            page,
            pageSize,
            onClose: () => setModal(null),
          }),
        disabled: !enabledStatus || expired,
      },
      {
        kind: 'Link',
        label: t('client.talon_one_promo_code.see_in_talonone'),
        url: row.externalLink,
        newTab: true,
      },
    ];
  };

  const columns: GridColDef[] = [
    { field: 'code', headerName: t('client.talon_one_promo_code.promo_code'), maxWidth: 120 },
    {
      field: 'campaignDescription',
      headerName: t('client.talon_one_promo_code.campaign_details'),
      renderCell: (params: GridCellParams) => (
        <>
          <div className={styles.textEllipsis}>{String(params.value)}</div>
          <Tooltip title={String(params.value)}>
            <div>
              <TooltipIcon className={styles.tooltipIcon} />
            </div>
          </Tooltip>
        </>
      ),
    },
    { field: 'campaignStatus', headerName: t('client.talon_one_promo_code.campaign_state') },
    { field: 'expiryDateTime', headerName: t('client.talon_one_promo_code.promo_code_expiry_date') },
    {
      field: 'status',
      headerName: t('client.talon_one_promo_code.promo_code_status'),
      valueGetter: (params: GridValueGetterParams) =>
        params.value === 'enabled'
          ? capitalizeFirstLetter(t('common.active'))
          : capitalizeFirstLetter(t('common.disabled')),
    },
    {
      field: 'action',
      headerName: '',
      maxWidth: 80,
      renderCell: (params: GridRenderCellParams) => {
        return <Menu options={menuOption(params.row)} />;
      },
    },
  ];

  const rows = talons
    ? talons.data.map((item, index) => ({
        ...item,
        id: index,
        campaignDescription: item.campaign.description,
        campaignStatus: capitalizeFirstLetter(item.campaign.status),
        expiryDateTime: transformDateString(item.expiryDateTime),
      }))
    : [];

  return (
    <>
      <Container
        title={t('client.talon_one_promo_code.title')}
        actions={actions}
        handler={{ open, setOpen }}
        testId="talon_one_promo_code"
      >
        <Table
          rows={rows}
          columns={columns}
          loading={isFetching}
          table={{
            kind: 'DynamicTable',
            totalItem: totalItems,
            pageSize,
            pageSizeOptions: [defaultPageSize, 50, 100],
            onPageChange,
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex !important',
              padding: '0.6rem 1rem !important',
              alignItems: 'center !important',
            },
            '& .MuiTablePagination-displayedRows': {
              display: 'none',
            },
          }}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
